import type { Dayjs } from 'dayjs';
import pluralize from 'pluralize';

import { toNumberWithSeparators } from '@cast/utils';

import { FREE_VCPU_CAP } from './reportingPlans';

/** `$249 / mo` or `$249/mo` → `$249/month` for long-form hints. */
export const formatReportingPlanPricePerMonth = (priceLabel: string): string =>
  priceLabel.replace(/\s*\/\s*mo\b/i, '/month');

export const formatReportingVcpu = (value: number): string =>
  toNumberWithSeparators(Math.round(value));

export const formatReportingVcpuCapHint = (
  vcpuCap: number | null | undefined,
  priceLabel: string
): string =>
  vcpuCap != null
    ? `Up to ${toNumberWithSeparators(
        vcpuCap
      )} vCPUs · ${formatReportingPlanPricePerMonth(priceLabel)}`
    : priceLabel;

/** Short date for Summary metrics (e.g. `9 October`). */
export const formatReportingTrialEndShort = (
  expiresAt?: Dayjs,
  expiresInDays?: number
): string | undefined => {
  if (expiresAt?.isValid()) {
    return expiresAt.format('D MMMM');
  }
  if (expiresInDays != null && Number.isFinite(expiresInDays)) {
    return `${expiresInDays} ${pluralize('day', expiresInDays)}`;
  }
  return undefined;
};

/** Long date for Plans banner (e.g. `9 October, 2026` or `in 5 days`). */
export const formatReportingTrialEndLong = (
  expiresAt?: Dayjs,
  expiresInDays?: number
): string | undefined => {
  if (expiresAt?.isValid()) {
    return expiresAt.format('D MMMM, YYYY');
  }
  if (expiresInDays != null && Number.isFinite(expiresInDays)) {
    return `in ${expiresInDays} ${pluralize('day', expiresInDays)}`;
  }
  return undefined;
};

export const REPORTING_CLUSTERS_EMPTY_TITLE =
  'Connect your cluster to get started';

export const REPORTING_CLUSTERS_EMPTY_BODY =
  'Add your first Kubernetes cluster to start your reporting trial and track costs across your fleet.';

export const DEFAULT_CLUSTERS_EMPTY_BODY =
  'Add your first Kubernetes cluster to monitor resources, optimize workloads, and track costs across your fleet.';

export const freeVcpuCapLabel = (): string =>
  toNumberWithSeparators(FREE_VCPU_CAP);
